import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { numberID } from "@/lib/format";
import { fmtWib } from "@/lib/bizdate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Boxes, FileText, History } from "lucide-react";

const Card = ({ label, value, tone = "slate", testId }) => (
  <div data-testid={testId} className={`rounded-xl border bg-white p-4 shadow-sm border-${tone}-200`}>
    <div className="text-xs font-medium text-slate-500">{label}</div>
    <div className={`mt-1 text-2xl font-semibold text-${tone}-700`}>{value}</div>
  </div>
);

const statusPill = (s) => s === "out"
  ? <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">Habis</span>
  : s === "low"
    ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Stok Rendah</span>
    : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Aman</span>;

export default function InventoryUmum() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({ categories: [], units: [], departments: [] });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("ALL");
  const [edit, setEdit] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [hist, setHist] = useState(null);
  const canManage = can("inventory.general.manage");

  const loadDash = () => api.get("/general-inventory/dashboard").then((r) => setDash(r.data)).catch(() => {});
  const loadItems = () => {
    setItems(null);
    const p = { status: "active" };
    if (q) p.q = q;
    if (cat !== "ALL") p.category = cat;
    api.get("/general-inventory/items", { params: p }).then((r) => setItems(r.data)).catch((e) => { toast.error(apiError(e)); setItems([]); });
  };
  useEffect(() => { loadDash(); api.get("/general-inventory/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(loadItems, [q, cat]);

  const openHistory = async (it) => {
    try { const r = await api.get(`/general-inventory/items/${it.id}/history`); setHist(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Inventory Umum" subtitle="General Supplies — barang operasional internal (ATK, pantry, cleaning, IT, maintenance, PPE). Bukan barang dagang." testId="geninv-header">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav("/purchasing/requests")} data-testid="geninv-create-pr-btn"><FileText className="mr-1.5 h-4 w-4" /> Buat PR</Button>
          {canManage && <Button onClick={() => { setEdit(null); setShowForm(true); }} data-testid="geninv-add-item-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Item Baru</Button>}
        </div>
      </PageHeader>

      {dash && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Card label="Total Item Aktif" value={dash.total_active_items} tone="indigo" testId="geninv-card-active" />
          <Card label="Stok Rendah" value={dash.low_stock} tone="amber" testId="geninv-card-low" />
          <Card label="Stok Habis" value={dash.out_of_stock} tone="rose" testId="geninv-card-out" />
          <Card label="Pemakaian Hari Ini" value={dash.internal_issues_today} tone="slate" testId="geninv-card-today" />
          <Card label="Pemakaian 30 Hari" value={dash.internal_issues_30d} tone="slate" testId="geninv-card-30d" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Cari item..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="geninv-search" />
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-56" data-testid="geninv-cat-filter"><SelectValue placeholder="Semua Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Semua Kategori</SelectItem>
            {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto" data-testid="geninv-item-table">
        {!items ? <Loading /> : items.length === 0 ? <EmptyState title="Belum ada item General Supplies" icon={Boxes} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Kode</TableHead><TableHead>Item</TableHead><TableHead>Kategori</TableHead><TableHead>Satuan</TableHead>
              <TableHead className="text-right">On Hand</TableHead><TableHead className="text-right">Min</TableHead>
              <TableHead>Status</TableHead><TableHead>Harga Acuan</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`geninv-row-${it.id}`}>
                  <TableCell className="font-mono text-xs">{it.code || "-"}</TableCell>
                  <TableCell className="font-medium">{it.name}</TableCell>
                  <TableCell className="text-slate-500 text-xs">{it.category || "-"}</TableCell>
                  <TableCell className="text-xs">{it.unit}</TableCell>
                  <TableCell className="text-right">{numberID(it.on_hand)}</TableCell>
                  <TableCell className="text-right text-slate-400">{numberID(it.minimum_stock)}</TableCell>
                  <TableCell>{statusPill(it.stock_status)}</TableCell>
                  <TableCell className="text-xs">{it.unit_cost != null ? `Rp${numberID(it.unit_cost)}` : <span className="text-slate-400">Cost belum tersedia</span>}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => openHistory(it)} data-testid={`geninv-hist-${it.id}`}><History className="h-4 w-4" /></Button>
                    {canManage && <Button variant="ghost" size="sm" onClick={() => { setEdit(it); setShowForm(true); }} data-testid={`geninv-edit-${it.id}`}>Edit</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {showForm && <ItemForm item={edit} meta={meta} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); loadItems(); loadDash(); }} />}
      {hist && <HistoryDialog data={hist} onClose={() => setHist(null)} />}
    </div>
  );
}

function ItemForm({ item, meta, onClose, onSaved }) {
  const isEdit = !!item;
  const [f, setF] = useState({
    name: item?.name || "", code: item?.code || "", category: item?.category || "",
    unit: item?.unit || "Pcs", minimum_stock: item?.minimum_stock ?? 0,
    location: item?.location || "", supplier: item?.supplier || "", notes: item?.notes || "",
    confirm_not_fixed_asset: false,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try {
      if (isEdit) await api.patch(`/general-inventory/items/${item.id}`, f);
      else await api.post("/general-inventory/items", f);
      toast.success(isEdit ? "Item diperbarui" : "Item dibuat (stok awal 0)");
      onSaved();
    } catch (e) {
      const msg = apiError(e);
      if (!isEdit && /Fixed Asset/i.test(msg)) { set("confirm_not_fixed_asset", true); toast.error(msg + " — klik Simpan lagi untuk konfirmasi."); return; }
      toast.error(msg);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="geninv-item-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Item" : "Item General Supplies Baru"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Nama Item *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="geninv-f-name" /></div>
          {!isEdit && <div><Label>Kode / SKU</Label><Input value={f.code} onChange={(e) => set("code", e.target.value)} data-testid="geninv-f-code" /></div>}
          <div>
            <Label>Kategori</Label>
            <Select value={f.category || "NONE"} onValueChange={(v) => set("category", v === "NONE" ? "" : v)}>
              <SelectTrigger data-testid="geninv-f-cat"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">(Tanpa kategori)</SelectItem>
                {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Satuan *</Label>
            <Select value={f.unit} onValueChange={(v) => set("unit", v)}>
              <SelectTrigger data-testid="geninv-f-unit"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Minimum Stok</Label><Input type="number" value={f.minimum_stock} onChange={(e) => set("minimum_stock", Number(e.target.value))} data-testid="geninv-f-min" /></div>
          <div><Label>Lokasi / Rak</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="geninv-f-loc" /></div>
          <div><Label>Supplier Default</Label><Input value={f.supplier} onChange={(e) => set("supplier", e.target.value)} data-testid="geninv-f-sup" /></div>
          <div className="col-span-2"><Label>Catatan</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="geninv-f-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-500" data-testid="geninv-f-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ data, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="geninv-history-dialog">
        <DialogHeader><DialogTitle>Riwayat — {data.item?.name}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-slate-200">
          {(!data.transactions || data.transactions.length === 0) ? <EmptyState title="Belum ada transaksi" icon={Boxes} /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Waktu (WIB)</TableHead><TableHead>Tipe</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead>Ref</TableHead><TableHead>Oleh</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.transactions.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-xs text-slate-500">{fmtWib(t.created_at)}</TableCell>
                    <TableCell className="text-xs font-medium">{t.type}</TableCell>
                    <TableCell className={`text-right ${t.qty < 0 ? "text-rose-600" : "text-emerald-600"}`}>{t.qty > 0 ? "+" : ""}{numberID(t.qty)}</TableCell>
                    <TableCell className="text-right text-slate-500">{numberID(t.qty_after)}</TableCell>
                    <TableCell className="font-mono text-xs">{t.reference || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{t.created_by_name || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
